import { useMemo, useState } from 'react';
import { Calendar } from 'lucide-react';

// Tauler Kanban de seguiment de projectes (2026-10-04, mogut a Planificació > Kanban el
// 2026-10-08) — extret d'Expedients.tsx perque Planificacio.tsx el reutilitzés tal qual.
// Des del 2026-10-08 una tarjeta pot estar a VÀRIES columnes alhora (p.ex. un projecte en
// Compres i en Fabricació taller a la vegada) — vegeu project_kanban_phases al backend.
// Arrossegar a una columna nova AFEGEIX aquella fase (sense treure les altres); per treure'n
// una concreta cal arrossegar la tarjeta fora del tauler, a la zona "Treure del tauler".

export interface KanbanBrand {
  id: string;
  primaryColor?: string;
}

// Nomes els camps que fa servir la targeta — qualsevol objecte Project "real" (Expedients.tsx)
// els te tots (són estructuralment compatibles).
export interface KanbanProject {
  id: string;
  projectNumber?: string;
  expedientNumber?: number;
  name: string;
  brandId?: string;
  brandCode?: string;
  clientName?: string;
  expectedEndDate?: string;
  // Totes les fases on el projecte es actualment actiu al Kanban (0 es tracta com ADJUDICAT,
  // igual que abans quan `status` no coincidia amb cap columna).
  phases: string[];
}

// Fases del Kanban — mateixos 7 codis que ProjectStatus al backend (Project.ts). CANCELLED
// queda fora del tauler (es veu des de la Taula/Llista, traient "Nomes actius").
export const KANBAN_COLUMNS: { key: string; label: string; headerClass: string }[] = [
  { key: 'ADJUDICAT',         label: "Adjudicat / Pendent d'iniciar", headerClass: 'bg-yellow-50 text-yellow-800 border-yellow-200' },
  { key: 'OFICINA_TECNICA',   label: 'Oficina tècnica',               headerClass: 'bg-blue-50 text-blue-800 border-blue-200' },
  { key: 'COMPRES',           label: 'Compres',                       headerClass: 'bg-amber-50 text-amber-800 border-amber-200' },
  { key: 'FABRICACIO_TALLER', label: 'Fabricació taller',             headerClass: 'bg-indigo-50 text-indigo-800 border-indigo-200' },
  { key: 'PREPARAT_MUNTATGE', label: 'Preparat per muntatge',         headerClass: 'bg-cyan-50 text-cyan-800 border-cyan-200' },
  { key: 'EN_MUNTATGE',       label: 'En muntatge',                   headerClass: 'bg-purple-50 text-purple-800 border-purple-200' },
  { key: 'TANCAMENT',         label: 'Tancament',                     headerClass: 'bg-green-50 text-green-800 border-green-200' },
];

const KANBAN_LABEL_OF: Record<string, string> = Object.fromEntries(KANBAN_COLUMNS.map((c) => [c.key, c.label]));

const formatDate = (dateStr: string | undefined | null): string => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('ca-ES');
};

export default function ProjectsKanbanBoard<P extends KanbanProject>({
  projects,
  brands,
  onCardClick,
  onAddPhase,
  onRemovePhase,
  updatingId,
}: {
  projects: P[];
  brands: KanbanBrand[];
  onCardClick: (project: P) => void;
  onAddPhase: (project: P, phase: string) => void;
  onRemovePhase: (project: P, phase: string) => void;
  updatingId: string | null;
}) {
  const [dragging, setDragging] = useState<{ project: P; phase: string } | null>(null);
  const [dragOverCol, setDragOverCol] = useState<string | null>(null);
  const [dragOverRemove, setDragOverRemove] = useState(false);

  const byColumn = useMemo(() => {
    const map: Record<string, P[]> = {};
    for (const col of KANBAN_COLUMNS) map[col.key] = [];
    for (const p of projects) {
      const phases = p.phases && p.phases.length > 0 ? p.phases : ['ADJUDICAT'];
      for (const phase of phases) {
        if (map[phase]) map[phase].push(p);
      }
    }
    return map;
  }, [projects]);

  return (
    <div className="space-y-2">
      <div className="flex gap-3 overflow-x-auto pb-2">
        {KANBAN_COLUMNS.map((col) => {
          const colProjects = byColumn[col.key] || [];
          return (
            <div
              key={col.key}
              onDragOver={(e) => { e.preventDefault(); setDragOverCol(col.key); }}
              onDragLeave={() => setDragOverCol((c) => (c === col.key ? null : c))}
              onDrop={(e) => {
                e.preventDefault();
                setDragOverCol(null);
                if (dragging && dragging.phase !== col.key) onAddPhase(dragging.project, col.key);
                setDragging(null);
              }}
              className={`flex flex-col shrink-0 w-64 rounded-lg border ${dragOverCol === col.key ? 'ring-2 ring-blue-400' : ''}`}
            >
              <div className={`px-3 py-2 rounded-t-lg border-b text-xs font-semibold flex items-center justify-between ${col.headerClass}`}>
                <span>{col.label}</span>
                <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full bg-white/70 text-[11px]">
                  {colProjects.length}
                </span>
              </div>
              <div className="flex-1 min-h-[120px] p-2 space-y-2 bg-gray-50 rounded-b-lg">
                {colProjects.length === 0 && (
                  <p className="text-[11px] text-muted-foreground italic px-1 py-2">Cap projecte</p>
                )}
                {colProjects.map((project) => {
                  const brand = brands.find((b) => b.id === project.brandId);
                  const otherPhases = (project.phases || []).filter((ph) => ph !== col.key);
                  return (
                    <div
                      key={`${project.id}-${col.key}`}
                      draggable
                      onDragStart={() => setDragging({ project, phase: col.key })}
                      onDragEnd={() => setDragging(null)}
                      onClick={() => onCardClick(project)}
                      className={`relative group bg-white rounded border border-gray-200 shadow-sm px-2.5 py-2 cursor-pointer hover:shadow-md transition-shadow ${updatingId === project.id ? 'opacity-50' : ''}`}
                    >
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onRemovePhase(project, col.key); }}
                        className="absolute top-1 right-1 w-4 h-4 flex items-center justify-center rounded text-gray-300 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity text-xs leading-none"
                        title={`Treure de ${col.label}`}
                      >
                        ×
                      </button>
                      <div className="flex items-center justify-between gap-1 mb-1 pr-4">
                        <span className="font-mono text-xs font-semibold text-blue-600">
                          {project.projectNumber || project.expedientNumber}
                        </span>
                        {project.brandCode && (
                          <span
                            className="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium text-white shrink-0"
                            style={{ backgroundColor: brand?.primaryColor || '#6b7280' }}
                          >
                            {project.brandCode}
                          </span>
                        )}
                      </div>
                      <p className="text-xs font-medium text-gray-900 line-clamp-2">{project.name}</p>
                      {project.clientName && (
                        <p className="text-[11px] text-muted-foreground mt-0.5">{project.clientName}</p>
                      )}
                      {project.expectedEndDate && (
                        <p className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1">
                          <Calendar className="h-3 w-3" /> {formatDate(project.expectedEndDate)}
                        </p>
                      )}
                      {otherPhases.length > 0 && (
                        <p className="text-[10px] text-blue-600 mt-1 italic truncate" title={otherPhases.map((ph) => KANBAN_LABEL_OF[ph] || ph).join(', ')}>
                          També a: {otherPhases.map((ph) => KANBAN_LABEL_OF[ph] || ph).join(', ')}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Zona per treure una tarjeta d'una columna concreta — nomes visible mentre s'arrossega,
          per no ocupar espai la resta del temps. */}
      {dragging && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOverRemove(true); }}
          onDragLeave={() => setDragOverRemove(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOverRemove(false);
            onRemovePhase(dragging.project, dragging.phase);
            setDragging(null);
          }}
          className={`flex items-center justify-center h-12 rounded-lg border-2 border-dashed text-xs font-medium transition-colors ${
            dragOverRemove ? 'border-red-400 bg-red-50 text-red-700' : 'border-gray-300 text-muted-foreground'
          }`}
        >
          Arrossega aquí per treure &quot;{dragging.project.name}&quot; de {KANBAN_LABEL_OF[dragging.phase] || dragging.phase}
        </div>
      )}
    </div>
  );
}
